import { useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import '../css/ProgressBar.css'

const TOTAL_ESTIMATE_MINUTES = 17.5

const ProgressBar = ({ progress, steps, currentStep, onStepClick }) => {
	const { t } = useTranslation('common')
	const [isOpen, setIsOpen] = useState(false)
	const stepTitle = step => t(`progressBar.steps.${step}`, { defaultValue: '' })

	const totalSteps = steps.length || 12
	const displayStep = currentStep > totalSteps ? totalSteps : currentStep
	const isFinished = currentStep > totalSteps
	const currentTitle = stepTitle(displayStep)
	const currentLabel = steps[Math.min(Math.max(displayStep - 1, 0), steps.length - 1)]?.label || ''

	const displayProgress =
		steps.length > 0
			? steps[Math.min(Math.max(displayStep - 1, 0), steps.length - 1)]?.position || 0
			: (displayStep / totalSteps) * 100

	const rawRemainingMinutes = TOTAL_ESTIMATE_MINUTES * (1 - displayProgress / 100)
	const remainingMinutes = Math.round(rawRemainingMinutes / 5) * 5

	return (
		<div className='progress-bar-container'>
			<motion.div
				className='progress-rail-panel'
				initial={{ opacity: 0, y: -10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
			>
				{}
				<button
					type='button'
					className='progress-summary-toggle'
					onClick={() => setIsOpen(prev => !prev)}
					aria-expanded={isOpen}
				>
					<span className='progress-summary-left'>
						<span className='progress-summary-badge'>{isFinished ? '✓' : currentLabel}</span>
						<span className='progress-summary-text'>
							{isFinished
								? t('progressBar.finishedLong')
								: `${t('progressBar.stepCount', { step: displayStep, total: totalSteps })} · ${currentTitle}`}
						</span>
					</span>
					<ChevronDown size={18} className={`progress-summary-chevron ${isOpen ? 'open' : ''}`} />
				</button>

				<div className='progress-summary-track'>
					<motion.div
						className='progress-summary-fill'
						initial={{ width: 0 }}
						animate={{ width: `${displayProgress}%` }}
						transition={{ duration: 0.6, ease: 'easeOut' }}
					/>
				</div>

				{!isFinished && (
					<span className='progress-time-remaining'>
						{remainingMinutes >= 5
							? t('progressBar.timeRemaining', { minutes: remainingMinutes })
							: t('progressBar.timeRemainingShort')}
					</span>
				)}

				{isOpen && (
					<div className='progress-step-list'>
						{steps.map((step, index) => {
							const stepNumber = index + 1
							const isCompleted = currentStep > stepNumber
							const isActive = currentStep === stepNumber

							let statusClass = 'future'
							if (isActive) statusClass = 'active'
							if (isCompleted) statusClass = 'completed'

							return (
								<button
									key={step.label}
									type='button'
									className={`progress-step-row ${statusClass}`}
									aria-current={isActive ? 'step' : undefined}
									disabled={!isCompleted}
									onClick={() => {
										if (isCompleted && onStepClick) {
											onStepClick(stepNumber)
											setIsOpen(false)
										}
									}}
								>
									<span className='progress-step-row-marker'>
										{isCompleted ? <Check size={13} strokeWidth={3} /> : stepNumber}
									</span>
									<span className='progress-step-row-label'>{stepTitle(stepNumber)}</span>
								</button>
							)
						})}
					</div>
				)}
			</motion.div>
		</div>
	)
}

export default ProgressBar
